import { animate, keyframes, style, transition, trigger, state } from '@angular/animations';

export const heightTransition = trigger('aniHeightTransition', [
  transition(':enter', [
    style({ opacity: 0, height: 0, overflow: 'hidden' }),
    animate('300ms ease-out', style({ opacity: 1, height: '*' })),
  ]),
  transition(':leave', [
    style({ opacity: 1, height: '*', overflow: 'hidden' }),
    animate('250ms ease-in', style({ height: 0, opacity: 0 })),
  ]),
]);

// Fade in/out for full panels so the background appears without moving the layout.
export const fadePanelTransition = trigger('fadePanelTransition', [
  transition(':enter', [style({ opacity: 0 }), animate('220ms ease-out', style({ opacity: 1 }))]),
  transition(':leave', [style({ opacity: 1 }), animate('160ms ease-in', style({ opacity: 0 }))]),
]);

// Foreground content movement for cards and callouts.
export const springContentTransition = trigger('springContentTransition', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translateY(24px) scale(0.985)' }),
    animate('360ms cubic-bezier(0.34, 1.56, 0.64, 1)', style({ opacity: 1, transform: 'translateY(0) scale(1)' })),
  ]),
  transition(':leave', [
    style({ opacity: 1, transform: 'translateY(0) scale(1)' }),
    animate('180ms ease-in', style({ opacity: 0, transform: 'translateY(10px) scale(0.99)' })),
  ]),
]);

export const BLINK_ANIMATION_DURATION = 4000;

// Legacy animation for new token on display
export const blinkAnimation = (triggerName = 'blinkAnimation') => {
  return trigger(triggerName, [
    transition(
      '* => 1',
      animate(
        BLINK_ANIMATION_DURATION,
        keyframes([
          style({ opacity: '0.25' }),
          style({ opacity: '1.0' }),
          style({ opacity: '0.25' }),
          style({ opacity: '1.0' }),
          style({ opacity: '0.25' }),
          style({ opacity: '1.0' }),
        ]),
      ),
    ),
  ]);
};

const getTokenFlashAnimationFrames = (repitions = 4) =>
  [...Array(repitions).keys()].reduce(
    (acc) => [...acc, style({ filter: 'brightness(1)' }), style({ filter: 'brightness(0.4)' })],
    [],
  );

// New animation for new token on display
export const tokenFlashAnimation = (triggerName = 'tokenFlashAnimation') => [
  trigger(triggerName, [transition('* => 1', animate('6000ms', keyframes(getTokenFlashAnimationFrames(8))))]),
];

export const tokenIntvertColorFlashAnimation = (triggerName = 'tokenIntvertColorFlashAnimation') => [
  trigger(triggerName, [
    state('flash1', style({ color: '{{fontColor}}', 'background-color': '{{bgColor}}' }), {
      params: { fontColor: '#ffffff', bgColor: '#2b2b2b' },
    }),
    state('flash2', style({ color: '{{bgColor}}', 'background-color': '{{fontColor}}' }), {
      params: { fontColor: '#ffffff', bgColor: '#2b2b2b' },
    }),
    state('original', style({ color: '{{fontColor}}', 'background-color': '{{bgColor}}' }), {
      params: { fontColor: '#ffffff', bgColor: '#2b2b2b' },
    }),
    transition('flash1 => flash2', animate('600ms')),
    transition('flash2 => flash1', animate('600ms')),
    transition('flash1 => original', animate('800ms')),
    transition('flash2 => original', animate('800ms')),
  ]),
];

const FLASH_DURATION = 6000;

export interface FlashItem {
  timeout?: ReturnType<typeof setTimeout>;
  flag: boolean;
  state: 'original' | 'flash1' | 'flash2';
}

export const triggerFlash = (item: FlashItem) => {
  if (item.timeout) clearTimeout(item.timeout);
  if (!item.flag) {
    item.flag = true;
    item.state = 'flash1';
  }
  item.timeout = setTimeout(() => {
    item.flag = false;
    item.state = 'original';
  }, FLASH_DURATION);
};

export const onFlashDone = (item: FlashItem) => {
  if (item.flag) {
    setTimeout(
      () => { item.state = item.state === 'flash1' ? 'flash2' : 'flash1'; },
      item.state === 'flash1' ? 500 : 0,
    );
  } else {
    item.state = 'original';
  }
};
